// Performance configuration for TV optimization
// NOTE: Performance monitoring classes only work in development mode

import { logger } from '@/utils/dev-logger';

const isDevelopment = process.env.NODE_ENV === 'development';

export const performanceConfig = {
  // Image optimization
  image: {
    lazyLoadRootMargin: '50px', // Reduced from 100px for faster loading
    maxCacheSize: 30, // Reduced cache size for TV memory constraints
    cacheMaxAge: 15 * 60 * 1000, // 15 minutes
    quality: {
      tv: { width: 1920, height: 1080, quality: 75 },
      thumbnail: { width: 320, height: 180, quality: 60 },
    },
  },

  // Network optimization
  network: {
    apiTimeout: 10000, // 10 seconds
    retryAttempts: 3,
    retryDelay: 1000, // Initial retry delay
    maxRetryDelay: 4000,
    concurrentRequests: 3, // Limit concurrent requests for TV
  },

  // Rendering optimization
  rendering: {
    virtualScrollThreshold: 20, // Items to render in virtual scroll
    debounceDelay: 0, // No debounce - instant response
    animationDuration: 0, // No animation - instant
    useCSSContainment: true, // Use CSS containment for better performance
  },

  // Memory management
  memory: {
    maxVideoInstances: 1, // Only one video player at a time
    cleanupInterval: 30000, // Cleanup unused resources every 30s
    maxDOMNodes: 1500, // Warning threshold for DOM nodes
  },

  // TV-specific optimizations
  tv: {
    enableGPUAcceleration: true,
    reducedMotion: false, // Can be toggled in settings
    prefetchDelay: 2000, // Delay before prefetching content
    focusDebounce: 0, // No debounce - instant focus response
  },
};

// Performance monitoring utilities
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, number[]> = new Map();

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  // Measure function execution time
  measure<T>(name: string, fn: () => T): T {
    // In production, just execute the function without measuring
    if (!isDevelopment) {
      return fn();
    }

    const start = performance.now();
    const result = fn();
    const duration = performance.now() - start;

    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }

    const measurements = this.metrics.get(name)!;
    measurements.push(duration);

    // Keep only last 100 measurements
    if (measurements.length > 100) {
      measurements.shift();
    }

    // Log slow operations (dev only)
    if (duration > 100) {
      logger.warn(`Slow operation detected: ${name} took ${duration.toFixed(2)}ms`);
    }

    return result;
  }

  // Get average execution time for a metric
  getAverage(name: string): number {
    const measurements = this.metrics.get(name);
    if (!measurements || measurements.length === 0) return 0;

    const sum = measurements.reduce((a, b) => a + b, 0);
    return sum / measurements.length;
  }

  // Get performance report
  getReport(): Record<string, { avg: number; max: number; min: number; count: number }> {
    const report: Record<string, any> = {};

    this.metrics.forEach((measurements, name) => {
      if (measurements.length > 0) {
        report[name] = {
          avg: this.getAverage(name),
          max: Math.max(...measurements),
          min: Math.min(...measurements),
          count: measurements.length,
        };
      }
    });

    return report;
  }

  // Clear all metrics
  clear(): void {
    this.metrics.clear();
  }
}

// Frame rate monitor for detecting performance issues
export class FrameRateMonitor {
  private lastTime = performance.now();
  private frames = 0;
  private fps = 0;
  private callback?: (fps: number) => void;
  private rafId?: number;

  start(callback?: (fps: number) => void): void {
    // Only work in development
    if (!isDevelopment) return;
    this.callback = callback;
    this.measure();
  }

  private measure = (): void => {
    const currentTime = performance.now();
    this.frames++;

    if (currentTime >= this.lastTime + 1000) {
      this.fps = Math.round((this.frames * 1000) / (currentTime - this.lastTime));
      this.frames = 0;
      this.lastTime = currentTime;

      // Warn if FPS drops below 30 (TV standard) - only in development
      if (isDevelopment && this.fps < 30 && this.fps > 0) {
        // Only warn if FPS is consistently low (not just during initial load)
        if (this.frames > 0) {
          logger.warn(`Low FPS detected: ${this.fps}`);
        }
      }

      if (this.callback) {
        this.callback(this.fps);
      }
    }

    this.rafId = requestAnimationFrame(this.measure);
  };

  stop(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }
  }

  getFPS(): number {
    return this.fps;
  }
}

// Memory monitor for detecting leaks
export class MemoryMonitor {
  private static instance: MemoryMonitor;
  private checkInterval?: number;
  private callback?: (memory: number) => void;

  static getInstance(): MemoryMonitor {
    if (!MemoryMonitor.instance) {
      MemoryMonitor.instance = new MemoryMonitor();
    }
    return MemoryMonitor.instance;
  }

  start(intervalMs = 30000, callback?: (memory: number) => void): void {
    // Only work in development
    if (!isDevelopment) return;
    if (this.checkInterval) return;

    this.callback = callback;
    this.checkInterval = window.setInterval(() => {
      this.checkMemory();
    }, intervalMs);

    // Initial check
    this.checkMemory();
  }

  stop(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
    this.callback = undefined;
  }

  private checkMemory(): void {
    // Only check in development
    if (!isDevelopment) return;
    if ('memory' in performance && (performance as any).memory) {
      const memory = (performance as any).memory;
      const usedMB = Math.round(memory.usedJSHeapSize / 1048576);
      const limitMB = Math.round(memory.jsHeapSizeLimit / 1048576);
      const percentage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;

      // Call callback with memory usage if provided
      if (this.callback) {
        this.callback(memory.usedJSHeapSize);
      }

      // Warn if memory usage is high (dev only)
      if (isDevelopment && percentage > 80) {
        logger.warn(`High memory usage: ${usedMB}MB / ${limitMB}MB (${percentage.toFixed(1)}%)`);
      }

      // Check DOM nodes (dev only)
      if (isDevelopment) {
        const nodeCount = document.getElementsByTagName('*').length;
        if (nodeCount > performanceConfig.memory.maxDOMNodes) {
          logger.warn(`High DOM node count: ${nodeCount}`);
        }
      }
    }
  }

  getMemoryInfo(): { used: number; limit: number; percentage: number } | null {
    // Only work in development
    if (!isDevelopment) return null;
    if ('memory' in performance && (performance as any).memory) {
      const memory = (performance as any).memory;
      return {
        used: Math.round(memory.usedJSHeapSize / 1048576),
        limit: Math.round(memory.jsHeapSizeLimit / 1048576),
        percentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
      };
    }
    return null;
  }
}

// Export singleton instances
export const performanceMonitor = PerformanceMonitor.getInstance();
export const frameRateMonitor = new FrameRateMonitor();
export const memoryMonitor = MemoryMonitor.getInstance();
